import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { FileRejection } from "react-dropzone";
import type { SubmitHandler } from "react-hook-form";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import FileUploadDropzone from "../../../components/file-upload-dropzone";
import OpenPadPass from "../../../components/icons/open-pad-pass";
import MaskedTextField from "../../../components/masked-text-field";
import { calculateFileSha256 } from "../../../utils/file-hash";

interface FormProps {
  hashCode: string;
  sha256: string;
}

function ValidateForm(): React.JSX.Element {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const methods = useForm<FormProps>({
    defaultValues: { hashCode: "" },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = methods;

  const onSubmit: SubmitHandler<FormProps> = (data): void => {
    navigate(`validate\\${data.hashCode || data.sha256}`);
  };

  const handleFileAccepted = async (acceptedFile: File): Promise<void> => {
    try {
      setLoading(true);
      const sha256 = await calculateFileSha256(acceptedFile);
      navigate(`validate\\${sha256}`);
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileRejected = (fileRejections: FileRejection[]) => {
    console.log("File rejected:", fileRejections);
  };

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        autoSave="off"
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      >
        <Container>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5">{t("home.title")}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack spacing={2}>
                <Typography variant="h6">{t("home.uploadSection")}</Typography>
                <FileUploadDropzone
                  isLoading={loading}
                  onFileAccepted={handleFileAccepted}
                  onFileRejected={handleFileRejected}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack spacing={2}>
                <Typography variant="h6">{t("home.codeSection")}</Typography>
                <Controller
                  control={control}
                  name="hashCode"
                  render={({ field, fieldState }) => {
                    const { error } = fieldState;
                    return (
                      <TextField
                        {...field}
                        helperText={error ? error.message : " "}
                        placeholder={t("home.codePlaceholder")}
                        slotProps={{
                          htmlInput: {
                            mask: "********-****-****-****-************",
                          },
                          input: {
                            inputComponent: MaskedTextField as never,
                            endAdornment: (
                              <InputAdornment position="end">
                                <OpenPadPass />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    );
                  }}
                  rules={{
                    required: t("home.codeRequired"),
                    pattern: {
                      value:
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      message: t("home.codeInvalid"),
                    },
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                disabled={!isValid}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                {t("home.submit")}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </form>
    </FormProvider>
  );
}

export default ValidateForm;
